import {Link, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {KairosMark} from '@/components/kairos-mark'
import {Hint} from '@/components/ui/hint'
import {PorQue} from '@/components/ui/por-que'
import {ExplicacionCorta} from '@/components/certificacion-explicacion'
import {DescargarEvidencia} from '@/components/descargar-evidencia'
import {MicroLabel} from '@/components/period-selector'
import {useOperatorPage} from '@/components/no-access-state'
import {
  isCertificacionArtefacto,
  useCertificacionContenido,
  type CertificacionArtefacto,
} from '@/hooks/sgsst/expediente/use-certificacion'
import {rutaDeDescarga, useEvidence} from '@/hooks/sgsst/evidencias/use-evidence'
import {formatDate} from '@/lib/risk'

const ORIGEN: Record<CertificacionArtefacto, string> = {
  matriz_riesgos: 'sgsst/matriz-riesgos',
  matriz_legal: 'sgsst/matriz-legal',
  indicadores: 'sgsst/indicadores',
  plan_trabajo: 'plan-trabajo',
  examenes: 'examenes',
}

/** La tabla scrollea dentro de su contenedor: la matriz de riesgos trae ~30 columnas GTC 45. */
function SeccionTabla({titulo, columnas, filas}: {titulo: string; columnas: string[]; filas: string[][]}) {
  const {t} = useTranslation()
  return (
    <section className="surface overflow-hidden">
      <div className="surface-divider border-b px-4 py-2.5">
        <MicroLabel>{titulo}</MicroLabel>
      </div>
      {filas.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          {t('certificacionContenido.seccionVacia')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-max text-sm">
            <thead>
              <tr className="surface-divider border-b">
                {columnas.map((columna, i) => (
                  <th
                    key={`${columna}-${i}`}
                    className="px-3 py-2 text-left font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                    {columna}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((fila, indice) => (
                <tr key={indice} className="border-b border-border/60 last:border-b-0">
                  {fila.map((celda, columna) => (
                    <td key={columna} className="max-w-xs px-3 py-2 align-top text-foreground">
                      {celda}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

/** El artefacto tal como quedó al certificar; la deriva posterior se ve en la pestaña de certificación. */
export function CertificacionContenidoPage() {
  const {t} = useTranslation()
  const {businessId, artefacto} = useParams<{
    businessId: string
    artefacto: string
  }>()
  const {guard} = useOperatorPage(businessId)
  const valido = isCertificacionArtefacto(artefacto)
  const {data, isLoading, isError} = useCertificacionContenido(
    valido ? businessId : undefined,
    valido ? artefacto : 'matriz_legal'
  )
  const {data: evidenceYear} = useEvidence(businessId)

  if (guard) return guard
  if (!businessId || !valido) return null

  const documento = data ? evidenceYear?.items.find((item) => item.id === data.evidencia_id) : undefined
  const volverA = `/businesses/${businessId}/${ORIGEN[artefacto]}`

  return (
    <div className="space-y-5" data-testid="certificacion-contenido-page">
      <div>
        <div className="flex items-center gap-2">
          <KairosMark />
          <MicroLabel>{t('certificacionContenido.eyebrow')}</MicroLabel>
        </div>
        <h1 className="mt-1 font-heading text-xl text-foreground">{t(`certificacionContenido.titulo.${artefacto}`)}</h1>
        <Link to={volverA} className="mt-1 inline-block text-sm text-primary hover:underline">
          {t('certificacionContenido.volver')}
        </Link>
      </div>

      {isLoading && <Skeleton className="h-60 w-full" data-testid="certificacion-contenido-loading" />}

      {isError && (
        <div className="surface-raised mx-auto max-w-lg p-8 text-center" data-testid="certificacion-contenido-empty">
          <h2 className="font-heading text-lg text-foreground">{t('certificacionContenido.empty.headline')}</h2>
        </div>
      )}

      {data && (
        <>
          <div
            className="surface space-y-2 border-l-[3px] border-l-primary p-4"
            data-testid="certificacion-contenido-sello">
            <div className="flex flex-wrap items-center gap-2">
              <Tag tone="neutral">{t('certificacion.version', {n: data.version})}</Tag>
              <Tag tone="neutral">
                {t('certificacion.vigenciaHasta', {
                  date: formatDate(data.vence_el),
                })}
              </Tag>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('certificacion.certificadoPor', {
                nombre: data.certificado_por,
                fecha: formatDate(data.certificado_en),
              })}
            </p>
            {/* U10: la hoja certificada se imprime y audita: la explicación va en línea, no en un popover. */}
            <Hint>{t('certificacionContenido.congelado')}</Hint>
            <PorQue
              label={t('certificacion.queEs.eyebrow')}
              titulo={t('certificacion.queEs.eyebrow')}
              contenidoTestId="certificacion-contenido-que-es-help"
              siempreVisible>
              <ExplicacionCorta />
            </PorQue>
            {documento && (
              <DescargarEvidencia
                ruta={rutaDeDescarga(businessId, documento.id)}
                className="inline-block text-sm font-medium text-primary hover:underline"
                testId="certificacion-contenido-descargar">
                {t('certificacion.descargar', {filename: documento.filename})}
              </DescargarEvidencia>
            )}
          </div>

          {data.secciones.length === 0 ? (
            <p className="notice-amber rounded-lg px-4 py-3 text-sm" data-testid="certificacion-contenido-sin-snapshot">
              {t('certificacionContenido.sinSnapshot')}
            </p>
          ) : (
            data.secciones.map((seccion) => (
              <SeccionTabla
                key={seccion.titulo}
                titulo={seccion.titulo}
                columnas={seccion.columnas}
                filas={seccion.filas}
              />
            ))
          )}
        </>
      )}
    </div>
  )
}
